import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { priceSettingList } from "../utils/api.services";
import QuantitySelector from "./QuantitySelector";

const DebrisRemovalModal = ({
  show,
  handleClose,
  handleSave,
  initialData,
  stateId,
  sqFt,
  isBtnLoading,
}) => {
  const [activeRes, setActiveRes] = useState("Dumpster");
  const [dumpsterPrice, setDumpsterPrice] = useState(0);
  const [dumpsterQuantity, setDumpsterQuantity] = useState(0);
  const [isDumpsterPriceEditable, setIsDumpsterPriceEditable] = useState(false);
  const [dumpTrailerPrice, setDumpTrailerPrice] = useState("");
  const [dumpTrailerQuantity, setDumpTrailerQuantity] = useState(0);
  const [defaultStatePrice, setDefaultStatePrice] = useState(0);
  const [underlaymentType, setUnderlaymentType] = useState("FELT_BUSTER");

  // Ventilation State
  const [ventilationType, setVentilationType] = useState("WHIRLYBIRD");
  const [whirlybirdSize, setWhirlybirdSize] = useState("12_IN");
  const [ventQuantity, setVentQuantity] = useState(1);
  const [ventPrice, setVentPrice] = useState(0);
  const [isVentPriceEditable, setIsVentPriceEditable] = useState(false);
  const [allPrices, setAllPrices] = useState([]);

  // Pipe Flashing State
  const [flashings, setFlashings] = useState({
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
  });

  const handleFlashingChange = (size, quantity) => {
    setFlashings((prev) => ({
      ...prev,
      [size]: quantity,
    }));
  };

  // Adjustable Pipe Flashing State
  const [adjustableFlashingQty, setAdjustableFlashingQty] = useState(0);

  // Valley Flashing State
  const [valleyFlashingType, setValleyFlashingType] = useState("NONE");
  const [valleyIceWater, setValleyIceWater] = useState("NONE");
  const [hipAndRidgeType, setHipAndRidgeType] = useState("TAB_SHINGLES");

  // Validation State & Refs
  const [errors, setErrors] = useState({});
  const ventilationRef = useRef(null);
  const valleyFlashingRef = useRef(null);
  const dumpTrailerPriceRef = useRef(null);

  useEffect(() => {
    if (show && initialData) {
      setActiveRes(initialData.debris_type || "Dumpster");
      if (initialData.debris_type === "Dumpster") {
        setDumpsterPrice(initialData.custom_price || 0);
        setDumpsterQuantity(initialData.custom_count || 0);
      } else if (initialData.debris_type === "Dump Trailer") {
        setDumpTrailerPrice(initialData.custom_price || "");
        setDumpTrailerQuantity(initialData.custom_count || 0);
      }
      setUnderlaymentType(initialData.underlayment_type || "FELT_BUSTER");
      setVentilationType(initialData.ventilation_type || "WHIRLYBIRD");
      setWhirlybirdSize(initialData.vent_size || "12_IN");
      setVentQuantity(initialData.vent_quantity || 1);
      setVentPrice(initialData.vent_unit_cost || 0);
      if (initialData.pipe_flashing) {
        setFlashings(
          typeof initialData.pipe_flashing === "string"
            ? JSON.parse(initialData.pipe_flashing)
            : initialData.pipe_flashing,
        );
      }
      setAdjustableFlashingQty(initialData.adjustable_pipe_flashing_qty || 0);
      setValleyFlashingType(initialData.valley_flashing_type || "NONE");
      setValleyIceWater(initialData.valley_ice_water || "NONE");
      setHipAndRidgeType(initialData.ridge_cap_type || "TAB_SHINGLES");
    }
  }, [show, initialData]);

  useEffect(() => {
    if (show && stateId) {
      const payload = {
        state_id: stateId,
      };
      priceSettingList(payload)
        .then((res) => {
          const priceData = res.data;
          if (priceData) {
            setAllPrices(priceData);
            const dumpsterData = priceData.find(
              (item) => item.unit === "Dumpster",
            );

            if (dumpsterData) {
              setDefaultStatePrice(dumpsterData.cost);
              // Do not overwrite user custom price if prefilling from initialData,
              // unless the initial custom price is 0
              if (!initialData || Number(initialData.custom_price) === 0) {
                setDumpsterPrice(dumpsterData.cost);
              }
            }
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [stateId, show, initialData]);

  // Track if we just loaded to prevent overwriting custom prices
  const [isVentPriceInitialized, setIsVentPriceInitialized] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVentPriceInitialized(false);
    }
  }, [show]);

  useEffect(() => {
    if (allPrices.length > 0) {
      if (initialData && !isVentPriceInitialized) {
        setIsVentPriceInitialized(true);
        return; // Skip setting from allPrices on initial load to preserve `custom` vent_unit_cost
      }

      let priceItem;
      if (ventilationType === "WHIRLYBIRD") {
        if (whirlybirdSize === "12_IN") {
          priceItem = allPrices.find(
            (item) => item.title === "12 in Whirlybird Turbine Roof Vent",
          );
        } else if (whirlybirdSize === "14_IN") {
          priceItem = allPrices.find(
            (item) => item.title === "14 in Whirlybird Turbine Roof Vent",
          );
        }
      } else if (ventilationType === "RIDGE_VENT") {
        priceItem = allPrices.find(
          (item) => item.title === "Roof Ridge Exhaust Vent",
        );
      }

      if (priceItem) {
        setVentPrice(priceItem.cost);
      } else {
        // Fallback or keep 0 if not found
        setVentPrice(0);
      }
    }
  }, [
    ventilationType,
    whirlybirdSize,
    allPrices,
    initialData,
    isVentPriceInitialized,
  ]);

  const onSave = () => {
    let newErrors = {};
    let hasError = false;

    if (!ventilationType) {
      newErrors.ventilationType = "Please select a ventilation type.";
      if (!hasError) {
        ventilationRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        hasError = true;
      }
    }
    if (!valleyFlashingType) {
      newErrors.valleyFlashingType = "Please select a Valley Flashing Type.";
      if (!hasError) {
        valleyFlashingRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        hasError = true;
      }
    }
    if (activeRes === "Dump Trailer" && !dumpTrailerPrice) {
      newErrors.dumpTrailerPrice =
        "Please enter a custom price for Dump Trailer.";
      if (!hasError) {
        dumpTrailerPriceRef.current?.focus();
        hasError = true;
      }
    }

    setErrors(newErrors);
    if (hasError) return;

    const priceDiffers = Number(dumpsterPrice) !== Number(defaultStatePrice);
    const finalDumpsterPrice = priceDiffers ? Number(dumpsterPrice) : 0;
    const finalDumpsterQty =
      Number(dumpsterQuantity) > 0 ? Number(dumpsterQuantity) : 0;
    const finalDumpTrailerQty =
      Number(dumpTrailerQuantity) > 0 ? Number(dumpTrailerQuantity) : 0;

    const data = {
      debris_type: activeRes,
      custom_price:
        activeRes === "Dumpster" ? finalDumpsterPrice : dumpTrailerPrice || 0,
      custom_count:
        activeRes === "Dumpster" ? finalDumpsterQty : finalDumpTrailerQty,
      underlayment_type: underlaymentType,
      ventilation_type: ventilationType,
      vent_size: ventilationType === "WHIRLYBIRD" ? whirlybirdSize : 0,
      vent_unit_cost: ventPrice,
      vent_price: ventPrice,
      vent_quantity:
        ventilationType === "WHIRLYBIRD" || ventilationType === "RIDGE_VENT"
          ? ventQuantity
          : 0,
      pipe_flashing: flashings,
      adjustable_pipe_flashing_qty: adjustableFlashingQty,
      valley_flashing_type: valleyFlashingType,
      valley_ice_water: valleyIceWater,
      ridge_cap_type: hipAndRidgeType,
    };
    handleSave(data);
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div
            className="modal-content"
            style={{ borderRadius: "15px", border: "none" }}
          >
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-4">
              {/* Debris Type Selection */}
              <div className="mb-4">
                <label className="form-label fw-bold text-dark mb-3">
                  SELECT DEBRIS TYPE
                </label>
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="debrisType"
                      id="dumpsterRadio"
                      checked={activeRes === "Dumpster"}
                      onChange={() => setActiveRes("Dumpster")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="dumpsterRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Dumpster
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="debrisType"
                      id="dumpTrailerRadio"
                      checked={activeRes === "Dump Trailer"}
                      onChange={() => setActiveRes("Dump Trailer")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="dumpTrailerRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Dump Trailer
                    </label>
                  </div>
                </div>
              </div>

              {/* Dynamic Fields based on Selection */}
              <div className="bg-light p-3 rounded-3">
                {activeRes === "Dumpster" ? (
                  <div className="animate__animated animate__fadeIn">
                    <div className="mb-4">
                      <label className="form-label fw-bold text-dark">
                        Price ($)
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          $
                        </span>
                        <input
                          type="number"
                          className="form-control border-start-0 ps-0"
                          value={dumpsterPrice}
                          readOnly={!isDumpsterPriceEditable}
                          onChange={(e) => setDumpsterPrice(e.target.value)}
                          style={{
                            boxShadow: "none",
                            outline: "none",
                          }}
                        />
                        <button
                          className="btn border"
                          type="button"
                          onClick={() =>
                            setIsDumpsterPriceEditable(!isDumpsterPriceEditable)
                          }
                        >
                          <FontAwesomeIcon
                            icon={isDumpsterPriceEditable ? faSave : faPencil}
                            className={
                              isDumpsterPriceEditable
                                ? "text-success"
                                : "text-primary"
                            }
                          />
                        </button>
                      </div>
                      <div className="form-text text-muted small mt-1">
                        <i className="bi bi-info-circle me-1"></i>
                        Based on selected state pricing.
                      </div>
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-bold text-dark">
                        Dumpster Quantity (Optional)
                      </label>
                      <QuantitySelector
                        value={dumpsterQuantity}
                        onChange={(val) => setDumpsterQuantity(val)}
                        min={0}
                        className="w-100"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="animate__animated animate__fadeIn">
                    <div className="mb-4">
                      <label className="form-label fw-bold text-dark">
                        Price ($)
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          $
                        </span>
                        <input
                          ref={dumpTrailerPriceRef}
                          type="number"
                          className={`form-control border-start-0 ps-0 ${
                            errors.dumpTrailerPrice ? "is-invalid" : ""
                          }`}
                          placeholder="Enter custom price"
                          value={dumpTrailerPrice}
                          onChange={(e) => {
                            setDumpTrailerPrice(e.target.value);
                            if (errors.dumpTrailerPrice) {
                              setErrors((prev) => ({
                                ...prev,
                                dumpTrailerPrice: null,
                              }));
                            }
                          }}
                          style={{
                            boxShadow: "none",
                            outline: "none",
                          }}
                        />
                      </div>
                      {errors.dumpTrailerPrice && (
                        <div className="text-danger small mt-1">
                          {errors.dumpTrailerPrice}
                        </div>
                      )}
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-bold text-dark">
                        Dump Trailer Quantity (Optional)
                      </label>
                      <QuantitySelector
                        value={dumpTrailerQuantity}
                        onChange={(val) => setDumpTrailerQuantity(val)}
                        min={0}
                        className="w-100"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Roof Underlayment Selection */}
              <div className="mb-4 mt-3">
                <label className="form-label fw-bold text-dark mb-3">
                  SELECT ROOF UNDERLAYMENT
                </label>
                <div className="d-flex flex-column gap-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="underlaymentType"
                      id="feltBusterRadio"
                      checked={underlaymentType === "FELT_BUSTER"}
                      onChange={() => setUnderlaymentType("FELT_BUSTER")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="feltBusterRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Felt Buster – Covers 990 sq ft / roll
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="underlaymentType"
                      id="stormGuardRadio"
                      checked={underlaymentType === "STORM_GUARD"}
                      onChange={() => setUnderlaymentType("STORM_GUARD")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="stormGuardRadio"
                      style={{ fontSize: "16px" }}
                    >
                      StormGuard Peel & Stick – Covers 190 sq ft / roll
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="underlaymentType"
                      id="iceWaterRadio"
                      checked={underlaymentType === "ICE_WATER"}
                      onChange={() => setUnderlaymentType("ICE_WATER")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="iceWaterRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Ice & Water Shield – Covers 210 sq ft / roll
                    </label>
                  </div>
                </div>
              </div>

              {/* Ventilation Selection */}
              <div className="mb-4 mt-3" ref={ventilationRef}>
                <label className="form-label fw-bold text-dark mb-3">
                  SELECT VENTILATION TYPE
                </label>
                <div className="d-flex gap-4 mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ventilationType"
                      id="whirlybirdRadio"
                      checked={ventilationType === "WHIRLYBIRD"}
                      onChange={() => {
                        setVentilationType("WHIRLYBIRD");
                        setVentQuantity(1)
                        if (errors.ventilationType) {
                          setErrors((prev) => ({
                            ...prev,
                            ventilationType: null,
                          }));
                        }
                      }}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="whirlybirdRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Whirlybird
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ventilationType"
                      id="ridgeVentRadio"
                      checked={ventilationType === "RIDGE_VENT"}
                      onChange={() => {
                        setVentilationType("RIDGE_VENT");
                        setVentQuantity(1)
                        if (errors.ventilationType) {
                          setErrors((prev) => ({
                            ...prev,
                            ventilationType: null,
                          }));
                        }
                      }}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="ridgeVentRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Ridge Vent
                    </label>
                  </div>
                </div>
                {errors.ventilationType && (
                  <div className="text-danger small mb-2">
                    {errors.ventilationType}
                  </div>
                )}

                {ventilationType === "WHIRLYBIRD" && (
                  <div className="bg-light p-3 rounded-3 animate__animated animate__fadeIn">
                    <div className="mb-3">
                      <label className="form-label fw-bold text-dark">
                        Select Size
                      </label>
                      <div className="d-flex gap-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="whirlybirdSize"
                            id="size12"
                            checked={whirlybirdSize === "12_IN"}
                            onChange={() => setWhirlybirdSize("12_IN")}
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="size12"
                          >
                            12 in
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="whirlybirdSize"
                            id="size14"
                            checked={whirlybirdSize === "14_IN"}
                            onChange={() => setWhirlybirdSize("14_IN")}
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="size14"
                          >
                            14 in
                          </label>
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="form-label fw-bold text-dark">
                          Price ($)
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white border-end-0">
                            $
                          </span>
                          <input
                            type="number"
                            className="form-control border-start-0 ps-0"
                            value={ventPrice}
                            readOnly={!isVentPriceEditable}
                            onChange={(e) => setVentPrice(e.target.value)}
                            style={{
                              boxShadow: "none",
                              outline: "none",
                            }}
                          />
                          <button
                            className="btn border"
                            type="button"
                            onClick={() =>
                              setIsVentPriceEditable(!isVentPriceEditable)
                            }
                          >
                            <FontAwesomeIcon
                              icon={isVentPriceEditable ? faSave : faPencil}
                              className={
                                isVentPriceEditable
                                  ? "text-success"
                                  : "text-primary"
                              }
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fw-bold text-dark">
                        Quantity
                      </label>
                      <QuantitySelector
                        value={ventQuantity}
                        onChange={setVentQuantity}
                        min={1}
                        max={100}
                        className="w-100"
                      />
                    </div>
                  </div>
                )}

                {ventilationType === "RIDGE_VENT" && (
                  <div className="bg-light p-3 rounded-3 animate__animated animate__fadeIn">
                    <div className="mb-3">
                      <label className="form-label fw-bold text-dark">
                        Price ($)
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          $
                        </span>
                        <input
                          type="number"
                          className="form-control border-start-0 ps-0"
                          value={ventPrice}
                          readOnly={!isVentPriceEditable}
                          onChange={(e) => setVentPrice(e.target.value)}
                          style={{
                            boxShadow: "none",
                            outline: "none",
                          }}
                        />
                        <button
                          className="btn border"
                          type="button"
                          onClick={() =>
                            setIsVentPriceEditable(!isVentPriceEditable)
                          }
                        >
                          <FontAwesomeIcon
                            icon={isVentPriceEditable ? faSave : faPencil}
                            className={
                              isVentPriceEditable
                                ? "text-success"
                                : "text-primary"
                            }
                          />
                        </button>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fw-bold text-dark">
                        Quantity
                      </label>
                      <QuantitySelector
                        value={ventQuantity}
                        onChange={setVentQuantity}
                        min={1}
                        max={100}
                        className="w-100"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Pipe Flashing Selection */}
              <div className="mb-4 mt-3">
                <label className="form-label fw-bold text-dark mb-3">
                  Select Galvanized Steel Pipe Flashing Type
                </label>
                <div className="bg-light p-3 rounded-3">
                  <div className="row g-3">
                    {[2, 3, 4, 5, 6].map((size) => (
                      <div key={size} className="col-6 col-md-4">
                        <label className="form-label fw-bold text-dark mb-1">
                          {size} in
                        </label>
                        <QuantitySelector
                          value={flashings[size]}
                          onChange={(val) => handleFlashingChange(size, val)}
                          min={0}
                          max={10}
                          width="30px"
                          height="30px"
                          className="w-100"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Adjustable Pipe Flashing Selection */}
              <div className="mb-4 mt-3">
                <label className="form-label fw-bold text-dark mb-3">
                  Adjustable Pipe Flashing
                </label>
                <div className="bg-light p-3 rounded-3">
                  <div className="mb-2">
                    <label className="form-label fw-bold text-dark">
                      Quantity (Optional)
                    </label>
                    <QuantitySelector
                      value={adjustableFlashingQty}
                      onChange={setAdjustableFlashingQty}
                      min={0}
                      max={50}
                      className="w-100"
                    />
                  </div>
                </div>
              </div>

              {/* Valley Flashing Selection */}
              <div className="mb-4 mt-3" ref={valleyFlashingRef}>
                <label className="form-label fw-bold text-dark mb-3">
                  Select Valley Flashing
                </label>
                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="valleyFlashingType"
                      id="galvanizedRadio"
                      checked={valleyFlashingType === "GALVANIZED"}
                      onChange={() => {
                        setValleyFlashingType("GALVANIZED");
                        if (errors.valleyFlashingType) {
                          setErrors((prev) => ({
                            ...prev,
                            valleyFlashingType: null,
                          }));
                        }
                      }}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="galvanizedRadio"
                      style={{ fontSize: "16px" }}
                    >
                      20 in × 50 ft Galvanized Steel Roll
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="valleyFlashingType"
                      id="aluminumRadio"
                      checked={valleyFlashingType === "ALUMINUM"}
                      onChange={() => {
                        setValleyFlashingType("ALUMINUM");
                        if (errors.valleyFlashingType) {
                          setErrors((prev) => ({
                            ...prev,
                            valleyFlashingType: null,
                          }));
                        }
                      }}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="aluminumRadio"
                      style={{ fontSize: "16px" }}
                    >
                      20 in × 50 ft Aluminum Roll
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="valleyFlashingType"
                      id="noneRadio"
                      checked={valleyFlashingType === "NONE"}
                      onChange={() => {
                        setValleyFlashingType("NONE");
                        if (errors.valleyFlashingType) {
                          setErrors((prev) => ({
                            ...prev,
                            valleyFlashingType: null,
                          }));
                        }
                      }}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="noneRadio"
                      style={{ fontSize: "16px" }}
                    >
                      None
                    </label>
                  </div>
                </div>
                {errors.valleyFlashingType && (
                  <div className="text-danger small">
                    {errors.valleyFlashingType}
                  </div>
                )}
              </div>

              {/* Valley Ice & Water Shield Selection */}
              <div className="mb-4 mt-3">
                <label className="form-label fw-bold text-dark mb-3">
                  Select Ice & Water Shield (for Valley)
                </label>
                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="valleyIceWater"
                      id="iceWaterYesRadio"
                      checked={valleyIceWater === "ICE_WATER"}
                      onChange={() => setValleyIceWater("ICE_WATER")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="iceWaterYesRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Ice & Water Shield 225 Sq Ft Self-Adhered
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="valleyIceWater"
                      id="iceWaterNoneRadio"
                      checked={valleyIceWater === "NONE"}
                      onChange={() => setValleyIceWater("NONE")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="iceWaterNoneRadio"
                      style={{ fontSize: "16px" }}
                    >
                      None
                    </label>
                  </div>
                </div>
              </div>

              {/* Hip and Ridge Selection */}
              <div className="mb-4 mt-3">
                <label className="form-label fw-bold text-dark mb-3">
                  Hip and Ridge selection
                </label>
                <div className="d-flex flex-column gap-2 mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="hipAndRidgeType"
                      id="tabShinglesRadio"
                      checked={hipAndRidgeType === "TAB_SHINGLES"}
                      onChange={() => setHipAndRidgeType("TAB_SHINGLES")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="tabShinglesRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Tab Shingles
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="hipAndRidgeType"
                      id="hipAndRidgeCapRadio"
                      checked={hipAndRidgeType === "HIP_AND_RIDGE_CAP"}
                      onChange={() => setHipAndRidgeType("HIP_AND_RIDGE_CAP")}
                      style={{ transform: "scale(1.2)" }}
                    />
                    <label
                      className="form-check-label fw-bold ms-2"
                      htmlFor="hipAndRidgeCapRadio"
                      style={{ fontSize: "16px" }}
                    >
                      Hip and Ridge Cap Roofing
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer flex gap-2 border-0 pt-0 px-4 pb-4">
              <button
                type="button"
                className="btn btn-light py-2 px-4 rounded-pill fw-bold"
                onClick={handleClose}
                disabled={isBtnLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn buttonColor py-2 px-4 rounded-pill fw-bold shadow-sm"
                onClick={onSave}
                disabled={isBtnLoading}
              >
                {isBtnLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Please Wait...
                  </>
                ) : (
                  "Save & Continue"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DebrisRemovalModal;
